import type { BlindConnection } from "./connection.js";
import { createBlindConnection } from "./connection.js";
import type { Endpoint } from "./endpoint.js";
import { relayWebSocketURL } from "./endpoint.js";
import { BlindBindError, asBlindBindError } from "./errors.js";
import { clientHandshake } from "./handshake.js";
import { AsyncQueue } from "./queue.js";
import type { WebSocketFactory } from "./websocket.js";
import { openWebSocket } from "./websocket.js";

const defaultHandshakeTimeoutMs = 10_000;

export interface DialOptions {
  endpoint: Endpoint;
  signal?: AbortSignal;
  handshakeTimeoutMs?: number;
  webSocketFactory?: WebSocketFactory;
}

export interface ReconnectPolicy {
  initialDelayMs: number;
  maxDelayMs: number;
  jitter: number;
}

export interface ConnectOptions extends DialOptions {
  reconnect?: Partial<ReconnectPolicy>;
}

export type BlindLinkState = "connecting" | "connected" | "reconnecting" | "suspended" | "closed";
export type BlindLinkStateListener = (state: BlindLinkState) => void;

export interface BlindLink extends AsyncIterable<BlindConnection> {
  readonly endpoint: Endpoint;
  readonly state: BlindLinkState;
  readonly current: BlindConnection | null;

  nextConnection(options?: { signal?: AbortSignal }): Promise<BlindConnection>;
  subscribe(listener: BlindLinkStateListener): () => void;
  suspend(): void;
  resume(): void;
  close(): void;
}

export async function dialOnce(options: DialOptions): Promise<BlindConnection> {
  const timeoutMs = positiveDuration(options.handshakeTimeoutMs ?? defaultHandshakeTimeoutMs, "Handshake timeout");
  const opened = await openWebSocket(
    relayWebSocketURL(options.endpoint, "connect", options.endpoint.bindId.toString()),
    {
      ...(options.signal ? { signal: options.signal } : {}),
      ...(options.webSocketFactory ? { factory: options.webSocketFactory } : {}),
      timeoutMs,
    },
  );
  const handshake = await clientHandshake(opened.socket, opened.inbox, options.endpoint.bindId, {
    ...(options.signal ? { signal: options.signal } : {}),
    timeoutMs,
  });
  return createBlindConnection(opened.socket, opened.inbox, options.endpoint, handshake);
}

export function connect(options: ConnectOptions): BlindLink {
  return new BlindLinkImpl(options);
}

class BlindLinkImpl implements BlindLink {
  readonly endpoint: Endpoint;
  readonly #options: ConnectOptions;
  readonly #policy: ReconnectPolicy;
  readonly #connections = new AsyncQueue<BlindConnection>();
  readonly #listeners = new Set<BlindLinkStateListener>();
  readonly #wakeWaiters = new Set<() => void>();
  #state: BlindLinkState = "connecting";
  #current: BlindConnection | null = null;
  #attempt: AbortController | null = null;
  #suspended = false;
  #closed = false;

  constructor(options: ConnectOptions) {
    this.endpoint = options.endpoint;
    this.#options = options;
    this.#policy = normalizeReconnectPolicy(options.reconnect);
    if (options.signal) {
      if (options.signal.aborted) {
        this.close();
      } else {
        options.signal.addEventListener("abort", () => this.close(), { once: true });
      }
    }
    void this.#run();
  }

  get state(): BlindLinkState {
    return this.#state;
  }

  get current(): BlindConnection | null {
    return this.#current?.state === "open" ? this.#current : null;
  }

  async nextConnection(options: { signal?: AbortSignal } = {}): Promise<BlindConnection> {
    for (;;) {
      const connection = await this.#connections.next(options);
      if (connection.state === "open") return connection;
    }
  }

  subscribe(listener: BlindLinkStateListener): () => void {
    this.#listeners.add(listener);
    listener(this.#state);
    return () => this.#listeners.delete(listener);
  }

  suspend(): void {
    if (this.#closed || this.#suspended) return;
    this.#suspended = true;
    this.#attempt?.abort();
    this.#current?.close({ reason: "suspended" });
    this.#setState("suspended");
    this.#wake();
  }

  resume(): void {
    if (this.#closed || !this.#suspended) return;
    this.#suspended = false;
    this.#setState("reconnecting");
    this.#wake();
  }

  close(): void {
    if (this.#closed) return;
    this.#closed = true;
    this.#attempt?.abort();
    this.#current?.close({ reason: "link closed" });
    this.#current = null;
    this.#connections.close(new BlindBindError("closed", "Blind link is closed"));
    this.#setState("closed");
    this.#wake();
  }

  async *[Symbol.asyncIterator](): AsyncIterator<BlindConnection> {
    while (!this.#closed) {
      try {
        yield await this.nextConnection();
      } catch (error) {
        if (this.#closed) return;
        throw error;
      }
    }
  }

  async #run(): Promise<void> {
    let connectedBefore = false;
    let retryDelayMs = this.#policy.initialDelayMs;
    while (!this.#closed) {
      if (this.#suspended) {
        await this.#waitForWake();
        continue;
      }
      this.#setState(connectedBefore ? "reconnecting" : "connecting");
      const attempt = new AbortController();
      this.#attempt = attempt;
      try {
        const connection = await dialOnce({
          endpoint: this.endpoint,
          signal: attempt.signal,
          ...(this.#options.handshakeTimeoutMs !== undefined
            ? { handshakeTimeoutMs: this.#options.handshakeTimeoutMs }
            : {}),
          ...(this.#options.webSocketFactory ? { webSocketFactory: this.#options.webSocketFactory } : {}),
        });
        if (this.#closed || this.#suspended) {
          connection.close();
          continue;
        }
        connectedBefore = true;
        retryDelayMs = this.#policy.initialDelayMs;
        this.#current = connection;
        this.#setState("connected");
        this.#connections.push(connection);
        await connection.closed;
        if (this.#current === connection) this.#current = null;
        if (!this.#closed && !this.#suspended) {
          await this.#waitForWake(jittered(this.#policy.initialDelayMs, this.#policy.jitter));
        }
      } catch (error) {
        if (this.#closed || this.#suspended || attempt.signal.aborted) continue;
        const normalized = asBlindBindError(error);
        if (!normalized.temporary) {
          this.#connections.close(normalized);
          this.#closed = true;
          this.#setState("closed");
          this.#wake();
          return;
        }
        const requestedDelay = normalized.retryAfterMs ?? retryDelayMs;
        await this.#waitForWake(jittered(requestedDelay, this.#policy.jitter));
        retryDelayMs = Math.min(retryDelayMs * 2, this.#policy.maxDelayMs);
      } finally {
        if (this.#attempt === attempt) this.#attempt = null;
      }
    }
  }

  #setState(state: BlindLinkState): void {
    if (this.#state === state) return;
    this.#state = state;
    for (const listener of this.#listeners) listener(state);
  }

  #waitForWake(delayMs?: number): Promise<void> {
    if (this.#closed) return Promise.resolve();
    return new Promise((resolve) => {
      let timeout: ReturnType<typeof setTimeout> | undefined;
      const finish = () => {
        if (timeout !== undefined) clearTimeout(timeout);
        this.#wakeWaiters.delete(finish);
        resolve();
      };
      this.#wakeWaiters.add(finish);
      if (delayMs !== undefined) timeout = setTimeout(finish, delayMs);
    });
  }

  #wake(): void {
    for (const finish of this.#wakeWaiters) finish();
  }
}

function normalizeReconnectPolicy(policy: Partial<ReconnectPolicy> | undefined): ReconnectPolicy {
  const initialDelayMs = nonNegativeDuration(policy?.initialDelayMs ?? 250, "Initial reconnect delay");
  const maxDelayMs = positiveDuration(policy?.maxDelayMs ?? 10_000, "Maximum reconnect delay");
  const jitter = policy?.jitter ?? 0.2;
  if (maxDelayMs < initialDelayMs) throw new Error("Maximum reconnect delay cannot be shorter than the initial delay");
  if (!Number.isFinite(jitter) || jitter < 0 || jitter > 1) throw new Error("Reconnect jitter must be between 0 and 1");
  return { initialDelayMs, maxDelayMs, jitter };
}

function jittered(delayMs: number, jitter: number): number {
  return Math.max(0, Math.round(delayMs * (1 + (Math.random() * 2 - 1) * jitter)));
}

function positiveDuration(value: number, label: string): number {
  if (!Number.isFinite(value) || value <= 0) throw new Error(`${label} must be positive`);
  return value;
}

function nonNegativeDuration(value: number, label: string): number {
  if (!Number.isFinite(value) || value < 0) throw new Error(`${label} cannot be negative`);
  return value;
}
